import Page from '..';
import PageExportsInvoices from './Invoices';
import PageExportsTaxes from './Taxes';
import PageExportsCompanies from './Companies';
import PageExportsDeposits from './Deposits';
import {
  Chart,
  ChartWrapperOptions,
  GoogleDataTableColumnRoleType,
} from 'react-google-charts';
import { useEffect, useRef, useState } from 'react';
import useExportsController from './useExportsController';
import { capitalizeFirstLetter } from '../../../store/utils';
import { renderToString } from 'react-dom/server';
import PageExportsLogistic from './Logistic';

const PageExports = () => {
  const {
    invoiceDashboard: getInvoiceDashboard,
    depositDashboard: getDepositDashboard,
    getInvoice,
  } = useExportsController();
  const contentRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = contentRef.current;
    const handleResize = (entries) => {
      for (let entry of entries) {
        setWidth(entry.contentRect.width);
      }
    };
    const resizeObserver = new ResizeObserver(handleResize);
    if (element) {
      resizeObserver.observe(element);
    }
    return () => {
      if (element) {
        resizeObserver.unobserve(element);
      }
    };
  }, []);

  const [rows, setRows] = useState<any[]>([]);

  const invoiceDashboard = getInvoice('dashboard.invoice.data');
  const depositDashboard = getInvoice('dashboard.deposit.data');

  useEffect(() => {
    Promise.all([getDepositDashboard(), getInvoiceDashboard()]);
  }, []);

  useEffect(() => {
    const uniqueMonths = [
      ...new Set(
        [...invoiceDashboard, ...depositDashboard]
          .sort(
            (a, b) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
          )
          .map(({ timestamp }) => {
            const date = new Date(timestamp);
            return date.toLocaleString('pt-BR', {
              year: 'numeric',
              month: 'long',
            });
          }),
      ),
    ];
    const rows = uniqueMonths.map((month) => {
      const invoiceIndex = invoiceDashboard.findIndex(({ timestamp }) => {
        return (
          new Date(timestamp).toLocaleString('pt-BR', {
            year: 'numeric',
            month: 'long',
          }) === month
        );
      });
      const invoice = invoiceDashboard[invoiceIndex];
      const depositIndex = depositDashboard.findIndex(({ timestamp }) => {
        return (
          new Date(timestamp).toLocaleString('pt-BR', {
            year: 'numeric',
            month: 'long',
          }) === month
        );
      });
      const deposit = depositDashboard[depositIndex];
      const invoiceValue = (invoice?.invoiceValue ?? 0) / 100;
      const realValue = (invoice?.realValue ?? 0) / 100;
      const depositValue = (deposit?.value ?? 0) / 100;
      const totalProfit = (invoice?.totalProfit ?? 0) / 100;
      const formatCurrency = (value: number) =>
        `${value < 0 ? '-' : ''}R$ ${Math.abs(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const generateTooltipString = () => {
        return renderToString(
          <div
            style={{
              padding: '0px 16px 16px 12px',
            }}
          >
            <h3
              style={{
                fontSize: '1.25rem',
                color: '#333',
              }}
            >
              {capitalizeFirstLetter(month)}
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                rowGap: '8px',
                columnGap: '16px',
                marginTop: '12px',
              }}
            >
              <div style={{ fontWeight: 'bold', color: '#555' }}>
                Valor da nota
              </div>
              <div style={{ textAlign: 'right', color: '#000' }}>
                {formatCurrency(invoiceValue)}
              </div>

              <div style={{ fontWeight: 'bold', color: '#555' }}>
                Valor real
              </div>
              <div style={{ textAlign: 'right', color: '#000' }}>
                {formatCurrency(realValue)}
              </div>
              <div style={{ fontWeight: 'bold', color: '#555' }}>Depósitos</div>
              <div style={{ textAlign: 'right', color: '#000' }}>
                {formatCurrency(depositValue)}
              </div>

              <div style={{ fontWeight: 'bold', color: '#555' }}>
                Saldo Dep. x Real
              </div>
              <div
                style={{
                  textAlign: 'right',
                  color: depositValue >= realValue ? '#28a745' : '#dc3545',
                }}
              >
                {formatCurrency(depositValue - realValue)}
              </div>

              <div style={{ fontWeight: 'bold', color: '#555' }}>
                Saldo Real x Nota
              </div>
              <div
                style={{
                  textAlign: 'right',
                  color: realValue >= invoiceValue ? '#28a745' : '#dc3545',
                }}
              >
                {formatCurrency(realValue - invoiceValue)}
              </div>
              <div style={{ fontWeight: 'bold', color: '#555' }}>
                Lucro total
              </div>
              <div
                style={{
                  textAlign: 'right',
                  color: totalProfit >= 0 ? '#28a745' : '#dc3545',
                }}
              >
                {formatCurrency(totalProfit)}
              </div>
            </div>
          </div>,
        );
      };
      return [
        capitalizeFirstLetter(month),
        {
          v: invoiceValue,
          f: formatCurrency(invoiceValue),
        },
        generateTooltipString(),
        {
          v: realValue,
          f: formatCurrency(realValue),
        },
        generateTooltipString(),
        {
          v: depositValue,
          f: formatCurrency(depositValue),
        },
        generateTooltipString(),
        {
          v: totalProfit,
          f: formatCurrency(totalProfit),
        },
        generateTooltipString(),
      ];
    });

    setRows(rows);
  }, [invoiceDashboard, depositDashboard]);

  const options: ChartWrapperOptions['options'] = {
    tooltip: { isHtml: true },
    legend: { position: 'bottom', maxLines: 2 },
    colors: ['#5C3292', '#437fff', '#1A8763', '#FF7043'],
    interpolateNulls: false,
    hAxis: {
      gridlines: { count: 7 },
    },
    vAxis: {
      format: 'currency',
    },
    seriesType: 'bars',
    annotations: {
      textStyle: {
        color: 'black',
        // fontSize: 16,
      },
      alwaysOutside: true,
    },
  };

  return (
    <Page className="page-banner">
      <div
        ref={contentRef}
        style={{
          position: 'relative',
          transition: 'min-width 500ms, max-width 500ms',
          width: '100%',
          minHeight: 'calc(100vh - 130px)',
          maxHeight: 'calc(100vh - 130px)',
          margin: '0 1rem',
          background: '#DDDDDD',
          borderRadius: '5px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'relative',
            display: 'flex',
            height: '100%',
            backgroundColor: 'white',
          }}
        >
          <Chart
            chartType="ColumnChart"
            chartLanguage="pt-BR"
            width={width}
            height="100%"
            columns={[
              'Mês',
              'Valor da nota',
              {
                type: 'string',
                role: GoogleDataTableColumnRoleType.tooltip,
                p: { html: true },
              },
              'Valor real',
              {
                type: 'string',
                role: GoogleDataTableColumnRoleType.tooltip,
                p: { html: true },
              },
              'Depósitos',
              {
                type: 'string',
                role: GoogleDataTableColumnRoleType.tooltip,
                p: { html: true },
              },
              'Lucro total',
              {
                type: 'string',
                role: GoogleDataTableColumnRoleType.tooltip,
                p: { html: true },
              },
            ]}
            rows={rows}
            options={options}
          />
        </div>
      </div>
    </Page>
  );
};

export default Object.assign(PageExports, {
  Invoices: PageExportsInvoices,
  Taxes: PageExportsTaxes,
  Companies: PageExportsCompanies,
  Deposits: PageExportsDeposits,
  Logistic: PageExportsLogistic,
});
